/**
 * AirNow API — real-time air quality data.
 * Free API key required (register at docs.airnowapi.org).
 */
import type { DataProvider, ProviderGeoContext, ProviderResult } from './types';
import { CACHE_TTL, fetchWithTimeout } from './types';

export interface AirQualityData {
  aqi: number;              // Air Quality Index (0-500)
  category: string;         // "Good", "Moderate", "Unhealthy for Sensitive Groups", etc.
  primaryPollutant: string; // "PM2.5", "O3", etc.
  pm25?: number;            // µg/m³
}

/**
 * AQF-1 — truthful failure classification for AirNow HTTP errors.
 *
 * Production incident (Redhead SED, 2026-06-10T14:59Z): the provider threw
 * a bare `AirNow: 401`, which `classifyProviderFailure` buckets as
 * `misconfigured` → "Provider integration is broken — admin attention
 * required" in SEDs and reports. The org key was VALID the whole time
 * (same key returned 200 before and after, never rotated): AirNow returns
 * HTTP 401 ("Invalid API key") BOTH for genuinely bad keys AND for
 * temporary per-key rate-limit suspensions (the API is throttled per
 * hour), so a transient throttle was mislabelled as a broken integration
 * and persisted into a report generated 17 hours later.
 *
 * This helper maps the observable auth surface onto the provider-registry
 * failure taxonomy via the self-declared error strings
 * `classifyProviderFailure` already understands:
 *
 *   - proxy 424 (org env var unbound) ........... → auth-missing
 *   - 401/403 and NO key was available .......... → auth-missing
 *   - browser 401/403 with no X-DA-Key-Source ... → transient-failure
 *     (the PROXY rejected the call — expired Firebase session — the
 *     upstream was never reached; retry/sweep will recover)
 *   - 401/403 on a KEYED request ................ → rate-limited
 *     (honest degraded signal: AirNow conflates throttling with bad
 *     keys; the sweeper's rate-limited backoff matches AirNow's hourly
 *     window and recovers automatically. A genuinely revoked key keeps
 *     surfacing as an honest airQuality gap — never fabricated data.)
 *   - anything else ............................. → `AirNow: <status>`
 *     (unchanged; 5xx/network stay transient-failure)
 *
 * Pure + exported so the routing matrix is unit-testable without fetch.
 */
export function airnowFailureMessage(
  status: number,
  keySource: string | null,
  runtime: { isServerRuntime: boolean; serverKeyPresent: boolean },
): string {
  if (status === 424) {
    // Proxy refused to forward: org key not bound (server env) and no
    // personal key. 'API key not configured' → auth-missing.
    return `AirNow API key not configured — proxy reported missing AIRNOW_API_KEY (HTTP ${status})`;
  }
  if (status === 401 || status === 403) {
    if (runtime.isServerRuntime) {
      return runtime.serverKeyPresent
        ? `AirNow rejected the keyed request (HTTP ${status}) — AirNow returns this for hourly rate limit suspensions as well as rotated keys; backing off`
        : `AirNow API key not configured — AIRNOW_API_KEY is unset in this runtime (HTTP ${status})`;
    }
    if (keySource === 'none') {
      return `AirNow API key not configured — proxy had no key to inject (HTTP ${status})`;
    }
    if (keySource == null) {
      // The proxy sets X-DA-Key-Source only after auth + allowlist pass;
      // its absence on a 401/403 means the PROXY itself rejected the call
      // (expired Firebase session) and AirNow was never consulted.
      return `AirNow request rejected before reaching upstream (HTTP ${status}) — proxy session expired; retry will recover`;
    }
    return `AirNow rejected the keyed request (HTTP ${status}) — AirNow returns this for hourly rate limit suspensions as well as rotated keys; backing off`;
  }
  return `AirNow: ${status}`;
}

/** Normalize the current ZIP/coordinate response without inventing an AQI.
 * Legacy rows remain readable for retained upstream fixtures.
 * The current fields used here expose AQI, not PM2.5 concentration.
 */
function normalizeAirNowObservation(value: unknown): AirQualityData {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error('AirNow returned non-numeric or malformed observation data');
  }
  const row = value as Record<string, unknown>;
  const current = Object.prototype.hasOwnProperty.call(row, 'nowcastAQI');
  const aqi = current ? row.nowcastAQI : row.AQI;
  if (typeof aqi !== 'number' || !Number.isFinite(aqi) || aqi < 0) {
    throw new Error('AirNow returned non-numeric or malformed observation data');
  }
  const legacyCategory = typeof row.Category === 'object' && row.Category !== null
    ? (row.Category as Record<string, unknown>).Name
    : undefined;
  const category = current ? row.aqiCategoryName : legacyCategory;
  const pollutant = current ? row.parameterName : row.ParameterName;
  if (current && (typeof category !== 'string' || !category.trim()
    || typeof pollutant !== 'string' || !pollutant.trim())) {
    throw new Error('AirNow returned non-numeric or malformed observation data');
  }
  // Do not mistake a PM2.5 AQI for a concentration. Only the legacy Conc
  // field explicitly carries the optional concentration used downstream.
  const pm25 = !current && pollutant === 'PM2.5' && typeof row.Conc === 'number'
    && Number.isFinite(row.Conc) ? row.Conc : undefined;
  return {
    aqi,
    category: typeof category === 'string' ? category : 'Unknown',
    primaryPollutant: typeof pollutant === 'string' ? pollutant : 'Unknown',
    ...(pm25 !== undefined ? { pm25 } : {}),
  };
}

export const airnowProvider: DataProvider<AirQualityData> = {
  id: 'airnow-aqi',
  name: 'AirNow Air Quality (EPA)',
  cacheTtlMs: CACHE_TTL.AIR_QUALITY,
  requiresKey: false, // Key injected server-side by proxy

  async fetch(lat, lng, _apiKey?: string, geo?: ProviderGeoContext): Promise<ProviderResult<AirQualityData>> {
    // CA3: `(lat, lng)` is the boundary centroid when a polygon is drawn
    // (`airnow-aqi` is in CENTROID_QUERY_PROVIDER_IDS), else the geocoded
    // address. AirNow returns the closest monitoring station within 50 mi
    // of the query point — a different point can resolve a different station
    // (hence a different AQI/PM2.5), so the parcel centroid is the right
    // origin.
    // API_KEY injected by server proxy (SERVER_API_KEYS['www.airnowapi.org'])
    // AirNow retired the separate latLong/ZIP current routes on 2026-09-30.
    const url = `https://www.airnowapi.org/aq/observation/current/ziplatlong/?format=application/json&latitude=${lat}&longitude=${lng}`;
    const res = await fetchWithTimeout(url, 15_000, geo?.signal);
    if (!res.ok) {
      // AQF-1 — classify auth-surface failures truthfully (see helper
      // docblock). Mirrors `fetchWithTimeout`'s runtime split: server/job
      // runtimes call AirNow directly with the env key; browsers go via
      // /api/proxy which stamps X-DA-Key-Source on forwarded responses.
      const keySource = typeof res.headers?.get === 'function'
        ? res.headers.get('X-DA-Key-Source')
        : null;
      const isServerRuntime = typeof window === 'undefined' && !process.env.VITEST;
      const serverKeyPresent = typeof process !== 'undefined' && Boolean(process.env?.AIRNOW_API_KEY);
      throw new Error(airnowFailureMessage(res.status, keySource, { isServerRuntime, serverKeyPresent }));
    }
    const json = await res.json();

    if (!Array.isArray(json)) throw new Error('AirNow returned non-numeric or malformed observation data');
    if (json.length === 0) throw new Error('AirNow returned no data');

    // Normalize first: reducing the legacy AQI key on the new NowCast
    // payload would silently fabricate zero/Unknown success. Any malformed
    // observation remains a failed collection, rather than hiding a gap.
    const readings = json.map(normalizeAirNowObservation);
    const worst = readings.reduce((a, b) => b.aqi > a.aqi ? b : a);
    const pm25Reading = readings.find(r => r.primaryPollutant === 'PM2.5')?.pm25;

    return {
      data: {
        aqi: worst.aqi,
        category: worst.category,
        primaryPollutant: worst.primaryPollutant,
        ...(typeof pm25Reading === 'number' ? { pm25: pm25Reading } : {}),
      },
      sources: [{
        field: 'airQuality',
        source: `AirNow EPA — AQI ${worst.aqi} (${worst.category}), primary pollutant: ${worst.primaryPollutant}`,
        url: 'https://www.airnow.gov/',
        retrievedAt: new Date().toISOString(),
      }],
    };
  },
};
